import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle, Clock, BookOpen, Sparkles, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { CefrBadge } from '@/components/CefrBadge'
import { WordPopup } from '@/components/reading/WordPopup'
import { ArticleGeneratedDialog } from '@/components/reading/ArticleGeneratedDialog'
import { extractSentenceContext, extractSentenceContextAtOffset } from '@/components/reading/extractSentenceContext'
import { readMultiWordSelection } from '@/components/reading/readMultiWordSelection'
import {
  findCoveringPhraseIndices,
  findFirstPhraseIndices,
} from '@/components/reading/findCoveringPhraseIndices'
import { tokenizeContent } from '@/lib/tokenizeContent'
import { articlesApi } from '@/api/articles'
import { learningApi } from '@/api/learning'
import { vocabularyApi } from '@/api/vocabulary'
import { KeepLearningEndlessFlow } from '@/components/learning/KeepLearningEndlessFlow'
import { MiniSessionPrompt } from '@/components/learning/MiniSessionPrompt'
import { useAiJobPoller, isPracticeResult } from '@/hooks/useAiJobPoller'
import { ComprehensionFlow } from '@/components/reading/ComprehensionFlow'
import type { CefrLevel } from '@/types/common'
import type { AiPracticeResult } from '@/types/ai'
import { copy } from '@/lib/copy'
import { AI_CREDIT_COSTS } from '@/lib/aiCredits'
import { useAiCredits } from '@/hooks/useAiCredits'
import { AiCreditCostBadge, aiCreditsExceededMessage } from '@/components/ai/AiCreditsIndicator'
import { cn } from '@/components/ui/cn'

interface PopupState {
  selection: string
  rect: DOMRect
  context: string
  /** Article token index for single-word taps; null for multi-word selection. */
  tokenIndex: number | null
}

type ComprehensionState = 'idle' | 'loading' | 'done' | 'error'

export function ArticleReaderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const learningSessionId = searchParams.get('learningSessionId')
  const learningStepIndex = searchParams.get('stepIndex')
  const keepLearning = searchParams.get('keepLearning') === '1'
  const fromGeneratedArticle = searchParams.get('generated') === '1'
  const wasGeneratedArticleRef = useRef(fromGeneratedArticle)
  const [showGeneratedDialog, setShowGeneratedDialog] = useState(fromGeneratedArticle)
  const queryClient = useQueryClient()
  const { recordCharge } = useAiCredits()
  const [popup, setPopup] = useState<PopupState | null>(null)
  /** Indices highlighted for the open popup (sticky until close). */
  const [activeTokenIndices, setActiveTokenIndices] = useState<Set<number>>(() => new Set())
  /** Brief entrance animation class; cleared after anim so sticky active remains. */
  const [flashTokenIndices, setFlashTokenIndices] = useState<Set<number>>(() => new Set())
  const flashAnimTimerRef = useRef<number | null>(null)
  const [comprehensionState, setComprehensionState] = useState<ComprehensionState>('idle')
  const [comprehensionJobId, setComprehensionJobId] = useState<string | null>(null)
  const [comprehensionResult, setComprehensionResult] = useState<AiPracticeResult | null>(null)
  const [comprehensionError, setComprehensionError] = useState<string | null>(null)
  const [addedWordsThisSession, setAddedWordsThisSession] = useState<Set<string>>(() => new Set())
  const [shownReEncounterHints, setShownReEncounterHints] = useState<Set<string>>(() => new Set())
  const [reEncounterHint, setReEncounterHint] = useState<string | null>(null)
  const articleContentRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (fromGeneratedArticle) {
      wasGeneratedArticleRef.current = true
      setShowGeneratedDialog(true)
    }
  }, [fromGeneratedArticle])

  const { data: article, isLoading } = useQuery({
    queryKey: ['article', id],
    queryFn: () => articlesApi.getArticle(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: knownForms } = useQuery({
    queryKey: ['knownForms'],
    queryFn: () => vocabularyApi.getKnownForms().then((r) => r.data.forms),
    staleTime: 1000 * 60 * 5,
  })

  const markReadMutation = useMutation({
    mutationFn: () => articlesApi.markAsRead(id!),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['article', id] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      queryClient.invalidateQueries({ queryKey: ['readingRecommended'] })
      if (learningSessionId && learningStepIndex != null) {
        await learningApi.completeStep(learningSessionId, Number(learningStepIndex))
        navigate(`/learn/${learningSessionId}`)
      }
    },
  })

  const continueSessionMutation = useMutation({
    mutationFn: async () => {
      if (!learningSessionId || learningStepIndex == null) return
      await learningApi.completeStep(learningSessionId, Number(learningStepIndex))
      navigate(`/learn/${learningSessionId}`)
    },
  })

  // ── Comprehension questions (AIPRAC-08) ───────────────────────────────────

  useAiJobPoller({
    jobId: comprehensionJobId,
    onDone: (response) => {
      if (isPracticeResult(response.result)) {
        setComprehensionResult(response.result)
        setComprehensionState('done')
      } else {
        setComprehensionError('Unexpected result type.')
        setComprehensionState('error')
      }
      setComprehensionJobId(null)
    },
    onError: (msg) => {
      setComprehensionError(msg)
      setComprehensionState('error')
      setComprehensionJobId(null)
    },
  })

  const handleGenerateComprehension = async () => {
    if (!id || comprehensionState === 'loading') return
    try {
      setComprehensionState('loading')
      setComprehensionResult(null)
      setComprehensionError(null)
      const res = await articlesApi.generateComprehension(id)
      await recordCharge(AI_CREDIT_COSTS.comprehensionGenerate)
      setComprehensionJobId(res.data.jobId)
    } catch (e: unknown) {
      const axiosErr = e as { response?: { status?: number; data?: { message?: string } } }
      const status = axiosErr?.response?.status
      setComprehensionError(
        status === 429
          ? aiCreditsExceededMessage(axiosErr?.response?.data?.message)
          : 'Could not start question generation. Please try again.'
      )
      setComprehensionState('error')
    }
  }

  const knownFormSet = useMemo(() => {
    const set = new Set((knownForms ?? []).map((w) => w.toLowerCase()))
    for (const word of addedWordsThisSession) {
      set.add(word.toLowerCase())
    }
    return set
  }, [knownForms, addedWordsThisSession])

  const tokens = useMemo(
    () => (article ? tokenizeContent(article.content) : []),
    [article?.content]
  )

  const tokenOffsets = useMemo(() => {
    const offsets: number[] = new Array(tokens.length)
    let cursor = 0
    for (let i = 0; i < tokens.length; i++) {
      offsets[i] = cursor
      cursor += tokens[i]!.text.length
    }
    return offsets
  }, [tokens])

  const handleWordClick = useCallback(
    (word: string, tokenIndex: number, e: React.MouseEvent<HTMLSpanElement>) => {
      const rect = (e.target as HTMLElement).getBoundingClientRect()
      const context = article?.content
        ? extractSentenceContextAtOffset(article.content, tokenOffsets[tokenIndex] ?? 0)
        : ''

      const inVocab = knownFormSet.has(word)
      if (
        inVocab &&
        addedWordsThisSession.has(word) &&
        !shownReEncounterHints.has(word)
      ) {
        setShownReEncounterHints((prev) => new Set(prev).add(word))
        setReEncounterHint(`You met "${word}" again`)
        window.setTimeout(() => setReEncounterHint(null), 3000)
      }

      if (popup?.selection === word && popup.tokenIndex === tokenIndex) {
        setPopup(null)
        setActiveTokenIndices(new Set())
        setFlashTokenIndices(new Set())
        return
      }
      setPopup({ selection: word, rect, context, tokenIndex })
      setActiveTokenIndices(new Set([tokenIndex]))
      if (flashAnimTimerRef.current != null) {
        window.clearTimeout(flashAnimTimerRef.current)
      }
      setFlashTokenIndices(new Set())
      window.requestAnimationFrame(() => {
        setFlashTokenIndices(new Set([tokenIndex]))
        flashAnimTimerRef.current = window.setTimeout(() => {
          setFlashTokenIndices(new Set())
          flashAnimTimerRef.current = null
        }, 1400)
      })
    },
    [article?.content, knownFormSet, addedWordsThisSession, shownReEncounterHints, tokenOffsets, popup]
  )

  const handleContentMouseUp = useCallback(() => {
    const container = articleContentRef.current
    if (!container || !article?.content) return

    const picked = readMultiWordSelection(container)
    if (!picked) return

    window.getSelection()?.removeAllRanges()
    setPopup({
      selection: picked.text,
      rect: picked.rect,
      context: extractSentenceContext(article.content, picked.text),
      tokenIndex: null,
    })
    // Phrase span settles via onSpanFlash; clear prior sticky until then.
    setActiveTokenIndices(new Set())
    setFlashTokenIndices(new Set())
  }, [article?.content])

  const handleSpanFlash = useCallback((phraseSurface: string) => {
    const indices =
      popup?.tokenIndex != null
        ? findCoveringPhraseIndices(tokens, popup.tokenIndex, phraseSurface)
        : findFirstPhraseIndices(tokens, phraseSurface)
    if (!indices?.length) {
      return
    }
    if (flashAnimTimerRef.current != null) {
      window.clearTimeout(flashAnimTimerRef.current)
    }
    const next = new Set(indices)
    setActiveTokenIndices(next)
    // Drop class first so a repeated flash restarts the CSS animation.
    setFlashTokenIndices(new Set())
    window.requestAnimationFrame(() => {
      setFlashTokenIndices(next)
      flashAnimTimerRef.current = window.setTimeout(() => {
        setFlashTokenIndices(new Set())
        flashAnimTimerRef.current = null
      }, 1400)
    })
  }, [popup?.tokenIndex, tokens])

  const closePopup = useCallback(() => {
    setPopup(null)
    setActiveTokenIndices(new Set())
    setFlashTokenIndices(new Set())
  }, [])

  useEffect(() => {
    return () => {
      if (flashAnimTimerRef.current != null) {
        window.clearTimeout(flashAnimTimerRef.current)
      }
    }
  }, [])
  const handleWordAdded = useCallback((word: string) => {
    const normalized = word.toLowerCase()
    setAddedWordsThisSession((prev) => new Set(prev).add(normalized))
    queryClient.invalidateQueries({ queryKey: ['exploreSession'] })
    void queryClient.invalidateQueries({ queryKey: ['knownForms'] })
  }, [queryClient])

  const dismissGeneratedDialog = useCallback(() => {
    setShowGeneratedDialog(false)
    if (!fromGeneratedArticle) return
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('generated')
    setSearchParams(nextParams, { replace: true })
  }, [fromGeneratedArticle, searchParams, setSearchParams])

  const goToLibrary = useCallback(() => {
    dismissGeneratedDialog()
    navigate('/reading?section=library')
  }, [dismissGeneratedDialog, navigate])

  const handleBack = useCallback(() => {
    if (wasGeneratedArticleRef.current) {
      navigate('/reading?section=library')
      return
    }
    navigate('/reading')
  }, [navigate])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl animate-pulse space-y-4 py-8">
        <div className="h-8 w-3/4 rounded bg-muted" />
        <div className="h-4 w-1/4 rounded bg-muted" />
        <div className="space-y-2 pt-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-4 rounded bg-muted" style={{ width: `${70 + Math.random() * 30}%` }} />
          ))}
        </div>
      </div>
    )
  }

  if (!article) return null

  return (
    <div className="mx-auto min-w-0 max-w-2xl overflow-x-hidden pb-16">
      {/* Back button */}
      <button
        onClick={handleBack}
        className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {fromGeneratedArticle || wasGeneratedArticleRef.current ? copy.reading.backToLibrary : 'Back to articles'}
      </button>

      {/* Article header */}
      <header className="mb-8">
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <CefrBadge level={article.cefrLevel as CefrLevel} />
          {article.estimatedTime && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {article.estimatedTime} min read
            </span>
          )}
          {article.isRead && (
            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle className="h-3.5 w-3.5" />
              Read
            </span>
          )}
        </div>

        <h1 className="mb-2 break-words text-2xl font-bold leading-snug text-foreground">
          {article.title}
        </h1>

        {article.author && (
          <p className="text-sm text-muted-foreground">By {article.author}</p>
        )}

        {/* Tags */}
        {article.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {article.tags.map(tag => (
              <span
                key={tag}
                className="rounded-full border border-border/60 bg-muted/50 px-2 py-0.5 text-xs font-medium text-foreground/80 capitalize"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Legend */}
      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-8 rounded border-b-2 border-primary bg-primary/20 dark:bg-primary/40" />
          <span>In your vocabulary</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-foreground underline decoration-dotted">word</span>
          <span>Click to see details</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-foreground underline decoration-primary/50">phrase</span>
          <span>Select multiple words to understand an expression</span>
        </div>
      </div>

      {/* Article content */}
      <article className="prose prose-slate dark:prose-invert max-w-none break-words text-base leading-8 selection:bg-primary/20">
        <p
          ref={articleContentRef}
          className="whitespace-pre-wrap"
          onMouseUp={handleContentMouseUp}
        >
          {tokens.map((token, i) => {
            if (!token.isWord) {
              return <span key={i}>{token.text}</span>
            }

            const inVocab = knownFormSet.has(token.lower)

            return (
              <span
                key={i}
                onClick={(e) => handleWordClick(token.lower, i, e)}
                data-word-clickable={inVocab ? undefined : 'true'}
                data-span-flash={activeTokenIndices.has(i) ? 'true' : undefined}
                className={cn(
                  inVocab
                    ? 'cursor-pointer rounded bg-primary/15 px-0.5 underline decoration-primary/60 decoration-2 transition-colors hover:bg-primary/25 dark:bg-primary/35 dark:decoration-primary dark:hover:bg-primary/50'
                    : 'cursor-pointer underline decoration-dotted decoration-muted-foreground/50 transition-colors hover:decoration-primary/70 dark:decoration-muted-foreground/80',
                  activeTokenIndices.has(i) && 'lexio-span-active',
                  flashTokenIndices.has(i) && 'lexio-span-flash',
                )}
              >
                {token.text}
              </span>
            )
          })}
        </p>
      </article>

      {/* Mark as read button */}
      {!article.isRead && (
        <div className="mt-10">
          <Button
            onClick={() => markReadMutation.mutate()}
            disabled={markReadMutation.isPending}
            className="min-h-12 w-full px-6 sm:w-auto"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            {markReadMutation.isPending ? 'Saving…' : copy.actions.markRead}
          </Button>
        </div>
      )}

      {article.isRead && learningSessionId && learningStepIndex != null ? (
        <div className="mt-10">
          <Button
            onClick={() => continueSessionMutation.mutate()}
            loading={continueSessionMutation.isPending}
            className="min-h-12 w-full px-6 sm:w-auto"
            data-testid="learning-continue-session"
          >
            {copy.actions.continueSession}
          </Button>
        </div>
      ) : null}

      {/* Re-encounter hint */}
      {reEncounterHint ? (
        <div
          className="fixed bottom-20 left-1/2 z-40 -translate-x-1/2 rounded-full border bg-card px-4 py-2 text-xs text-muted-foreground shadow-md"
          data-testid="re-encounter-hint"
        >
          {reEncounterHint}
        </div>
      ) : null}

      <KeepLearningEndlessFlow visible={keepLearning && article.isRead} />
      <MiniSessionPrompt inGuidedFlow={Boolean(learningSessionId)} />

      {/* AIPRAC-08 — Comprehension questions (shown after article is read) */}
      {article.isRead && (
        <section className="mt-10 rounded-xl border bg-card p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-2">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              <h2 className="text-sm font-semibold">{copy.comprehension.title}</h2>
            </div>

            {comprehensionState !== 'done' && (
              <Button
                className="min-h-12 w-full px-4 text-xs sm:h-8 sm:w-auto"
                disabled={comprehensionState === 'loading'}
                loading={comprehensionState === 'loading'}
                onClick={handleGenerateComprehension}
              >
                {comprehensionState !== 'loading' && <Sparkles className="h-3 w-3" />}
                {copy.actions.generateQuestions}
                {comprehensionState !== 'loading' ? (
                  <AiCreditCostBadge
                    cost={AI_CREDIT_COSTS.comprehensionGenerate}
                    className="ml-1 bg-primary-foreground/15 text-primary-foreground"
                  />
                ) : null}
              </Button>
            )}
          </div>

          {comprehensionState === 'idle' && (
            <p className="text-sm text-muted-foreground">
              {copy.actions.generateQuestionsHint}
            </p>
          )}

          {comprehensionState === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Generating questions…
            </div>
          )}

          {comprehensionState === 'error' && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{comprehensionError}</span>
              </div>
              <Button
                className="h-8 w-auto bg-secondary px-4 text-xs text-secondary-foreground hover:bg-secondary/80"
                onClick={() => setComprehensionState('idle')}
              >
                Try again
              </Button>
            </div>
          )}

          {comprehensionState === 'done' && comprehensionResult && id && (
            <ComprehensionFlow
              articleId={id}
              content={comprehensionResult.content}
              onRegenerate={handleGenerateComprehension}
            />
          )}
        </section>
      )}

      {/* Word popup */}
      {popup && id && (
        <WordPopup
          mode="article"
          selection={popup.selection}
          articleId={id}
          context={popup.context}
          anchorRect={popup.rect}
          onClose={closePopup}
          onWordAdded={handleWordAdded}
          onSpanFlash={handleSpanFlash}
        />
      )}

      {showGeneratedDialog ? (
        <ArticleGeneratedDialog
          onStartReading={dismissGeneratedDialog}
          onGoToLibrary={goToLibrary}
        />
      ) : null}
    </div>
  )
}

